import { IntelligenceLookupType, ISerpApiSearchResult } from "../models/PartLookup";

export interface IIntelligencePriceSummary {
  min: number;
  max: number;
  avg: number;
}

/** Whether the AI result filter took this listing out of the price figures. */
export function isExcluded(result: ISerpApiSearchResult): boolean {
  return !!result.excludedReason && result.excludedReason.trim().length > 0;
}

const LOOKUP_TYPE_LABELS: Record<IntelligenceLookupType, string> = {
  OePartNumber: "OE part number",
  PartNumber: "MPN",
  Vehicle: "make, model and year",
};

/**
 * What the lookup matched on and the term it searched, e.g. OE part number "1K0615301AA". The API
 * falls back from OE part number to MPN to vehicle and tags every result with the one that hit, so
 * the first result speaks for the set. Null when the results carry no lookup type.
 */
export function describeMatch(
  results: ISerpApiSearchResult[] | null | undefined,
): { label: string; term: string | null } | null {
  const first = results?.find((r) => r.matchedOn != null);
  if (!first?.matchedOn) return null;

  const label = LOOKUP_TYPE_LABELS[first.matchedOn];
  if (!label) return null;

  const term = first.searchTerm?.trim();
  return { label, term: term ? term : null };
}

/**
 * Min, max and mean asking price of the eBay listings Wilma Intelligence returned, rounded to the
 * penny. Listings without a positive price, and listings the AI result filter excluded, are ignored;
 * null when none are left. Matches the API's IntelligencePriceSummary, which stores the same figures
 * on the part.
 */
export function summarisePrices(
  results: ISerpApiSearchResult[] | null | undefined,
): IIntelligencePriceSummary | null {
  const prices = (results ?? [])
    .filter((r) => !isExcluded(r))
    .map((r) => r.priceExtracted)
    .filter((p): p is number => p != null && p > 0);

  if (prices.length === 0) return null;

  const round = (n: number) => Math.round(n * 100) / 100;

  return {
    min: round(Math.min(...prices)),
    max: round(Math.max(...prices)),
    avg: round(prices.reduce((sum, p) => sum + p, 0) / prices.length),
  };
}
